import React, { useEffect, useState, useCallback, useMemo } from "react";
import * as A from "./lib/api";
import { H1, H2, Sub, Card } from "./Pages";
import { toast } from "sonner";
import { Bot, Play, Loader2, ShieldCheck, CheckCircle2, AlertTriangle, Info, XCircle, Gauge, Network } from "lucide-react";

const SEV = {
  critical: { chip: "chip-amber", icon: XCircle, color: "var(--overdue)" },
  warn: { chip: "chip-amber", icon: AlertTriangle, color: "#8a5a00" },
  info: { chip: "chip-gray", icon: Info, color: "var(--muted)" },
  ok: { chip: "chip-green", icon: CheckCircle2, color: "var(--primary)" },
};

function Findings({ result }) {
  if (!result) return null;
  return (
    <div className="mt-2 space-y-1" data-testid="subagent-findings">
      {(result.findings || []).map((f, i) => {
        const s = SEV[f.severity] || SEV.info; const Icon = s.icon;
        return (
          <div key={i} className="flex items-start gap-2 text-sm">
            <Icon size={14} style={{ color: s.color, marginTop: 2 }} />
            <span>{f.message}</span>
          </div>
        );
      })}
      {result.ladder && <div className="text-xs mt-2 font-medium" style={{ color: "var(--primary)" }}>Readiness ladder: {result.ladder}</div>}
    </div>
  );
}

// ==================== SUB-AGENT CONSOLE ====================
export function SubAgentConsolePage() {
  const [agents, setAgents] = useState(null);
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState({});

  useEffect(() => { A.subagents.list().then((r) => setAgents(r.data)).catch(() => toast.error("Admins only")); }, []);

  const run = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try { const r = await A.subagents.run(id); setResults((s) => ({ ...s, [id]: r.data })); }
    catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };
  const runAll = async () => {
    if (!agents) return;
    for (const a of agents) { await run(a.id); }
    toast.success("All sub-agents run");
  };

  if (!agents) return <div>Loading…</div>;
  const cats = [...new Set(agents.map((a) => a.category))];

  return (
    <div data-testid="subagents-page">
      <H1>Sub-Agent Console</H1>
      <Sub>Fourteen deterministic, read-only governance services. Each inspects live portal data and reports findings — none change data or invent facts. Run one, or run them all for a full sweep.</Sub>
      <div className="flex justify-end mb-4">
        <button className="btn-gold flex items-center gap-1" onClick={runAll} data-testid="run-all-subagents"><Play size={14} /> Run all</button>
      </div>
      {cats.map((cat) => (
        <div key={cat} className="mb-6">
          <H2>{cat}</H2>
          <div className="grid md:grid-cols-2 gap-4">
            {agents.filter((a) => a.category === cat).map((a) => (
              <Card key={a.id} data-testid={`subagent-${a.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0">
                    <div className="font-medium flex items-center gap-2" style={{ color: "var(--primary)" }}>
                      {a.id === "release_gate" ? <Gauge size={15} /> : <Bot size={15} />} {a.name}
                    </div>
                    <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>{a.description}</div>
                  </div>
                  <button className="btn-outline text-xs flex items-center gap-1 shrink-0" onClick={() => run(a.id)} disabled={busy[a.id]} data-testid={`run-${a.id}`}>
                    {busy[a.id] ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Run
                  </button>
                </div>
                <Findings result={results[a.id]} />
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ==================== RELEASE GATE / FINAL ALL-PHASE AUDIT ====================
export function ReleaseGatePage() {
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);

  const runAudit = useCallback(async () => {
    setBusy(true);
    try { const r = await A.subagents.allPhaseAudit(); setAudit(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Admins only"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { runAudit(); }, [runAudit]);

  const ladderColor = (l) => l?.startsWith("READY") ? "chip-green" : l?.startsWith("REVIEW") ? "chip-amber" : "chip-amber";

  return (
    <div data-testid="release-gate-page">
      <H1>Final All-Phase Audit</H1>
      <Sub>The release gate before republication: a structural completion matrix across all phases plus the aggregated sub-agent readiness ladder. Legal and entity data remain owner-supplied — nothing is fabricated.</Sub>
      <div className="flex justify-end mb-4">
        <button className="btn-gold flex items-center gap-1" onClick={runAudit} disabled={busy} data-testid="run-audit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Re-run audit
        </button>
      </div>
      {!audit ? <div>Running audit…</div> : (
        <>
          <Card className="mb-4" data-testid="readiness-card">
            <div className="flex items-center justify-between">
              <div className="label-caps">Overall release readiness</div>
              <span className={`chip ${ladderColor(audit.release_readiness)}`} data-testid="readiness-ladder">{audit.release_readiness}</span>
            </div>
            <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>{audit.note}</div>
          </Card>
          <H2>Completion matrix</H2>
          <div className="space-y-2">
            {audit.matrix.map((m, i) => (
              <Card key={i} data-testid={`phase-row-${i}`}>
                <div className="flex justify-between items-center">
                  <div className="font-medium" style={{ color: "var(--primary)" }}>{m.phase}</div>
                  <span className="chip chip-green text-xs"><CheckCircle2 size={11} className="inline" /> {m.status}</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {m.checks.map((c) => (
                    <span key={c.collection} className="chip chip-gray text-xs">{c.collection}: {c.records}</span>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


// ==================== FEATURE DEPENDENCY MAP ====================
export function DependencyMapPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await A.subagents.dependencyMap(); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Admins only"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const nameById = useMemo(() => {
    if (!data) return {};
    return Object.fromEntries(data.features.map((f) => [f.id, f.name]));
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return {};
    const q = filter.trim().toLowerCase();
    const byLayer = {};
    for (const f of data.features) {
      if (q && !(f.name.toLowerCase().includes(q) || f.module.toLowerCase().includes(q) || f.id.toLowerCase().includes(q))) continue;
      if (!byLayer[f.layer]) byLayer[f.layer] = [];
      byLayer[f.layer].push(f);
    }
    return byLayer;
  }, [data, filter]);

  return (
    <div data-testid="dependency-map-page">
      <H1>Feature Dependency Map</H1>
      <Sub>Every portal feature, its backend module and collections, what it depends on (upstream), and what depends on it (downstream). Deterministic — no legal facts fabricated.</Sub>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <input aria-label="Filter features" placeholder="Filter by name, id, or module…" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="dep-filter" className="max-w-sm" />
        <button className="btn-gold flex items-center gap-1" onClick={load} disabled={busy} data-testid="reload-dep">
          {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Network size={14} aria-hidden="true" />} Reload
        </button>
      </div>
      {!data ? <div>Loading…</div> : (
        <>
          <Card className="mb-4">
            <div className="text-xs" style={{ color: "var(--muted)" }}>{data.note}</div>
            <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>As of {new Date(data.as_of).toLocaleString()} · {data.features.length} features across {data.layers.length} layers.</div>
          </Card>
          {data.layers.map((layer) => {
            const rows = filtered[layer];
            if (!rows || !rows.length) return null;
            return (
              <div key={layer} className="mb-6">
                <H2>{layer}</H2>
                <div className="grid md:grid-cols-2 gap-4">
                  {rows.map((f) => (
                    <Card key={f.id} data-testid={`dep-${f.id}`}>
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0">
                          <div className="font-medium" style={{ color: "var(--primary)" }}>{f.name}</div>
                          <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                            <code className="font-mono">{f.route}</code> · {f.module}
                          </div>
                        </div>
                      </div>
                      {f.collections.length > 0 && (
                        <div className="mt-2">
                          <div className="label-caps">Collections</div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {f.collections.map((c) => (
                              <span key={c} className="chip chip-gray text-xs">{c}: {f.record_counts[c]}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {f.depends_on.length > 0 && (
                        <div className="mt-2">
                          <div className="label-caps">Depends on</div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {f.depends_on.map((d) => (
                              <span key={d} className="chip chip-gold text-xs" title={nameById[d] || d}>{nameById[d] || d}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {f.used_by.length > 0 && (
                        <div className="mt-2">
                          <div className="label-caps">Used by</div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {f.used_by.map((d) => (
                              <span key={d} className="chip chip-green text-xs" title={nameById[d] || d}>{nameById[d] || d}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
